import React, { useContext, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useFormik from 'hooks/useFormik';
import ActionRow from 'components/ActionRow';
import Loader from 'components/Loaders/Loader';
import authService from 'store/auth/service';
import { Snackbar } from 'components/Snackbars';
import { DashboardPage } from 'routes/paths';
import { ContentBox } from 'components/ContentBox';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';
import { CompanyLogo } from 'components/CompanyLogo';
import { CompanyContext } from 'index';
import useSelector from 'hooks/useSelector';
import { rolesSelector, tokenSelector } from 'store/user/selectors';
import { UserRoleEnum } from 'common/user/enums/user-role.enum';

import { validationSchema } from './validationSchema';
import styles from './styles.module.scss';
import { useQueryParams } from '../../../hooks/useSearchParams';
import { IUser } from '../../../store/user/interfaces';

export const LoginPage = () => {
  const userOnline = useSelector(tokenSelector);
  const userRoles = useSelector(rolesSelector);
  const { t } = useTranslation();
  const company = useContext(CompanyContext);
  const queryParams = useQueryParams();

  const [confirmToken, { isLoading }] = authService.useConfirmTokenMutation();
  const [getSSOUrl] = authService.useGetSSOUrlMutation();

  const isValidAuthorizedManager =
    !!userOnline && !!userRoles && userRoles.includes(UserRoleEnum.MANAGER);

  useEffect(() => {
    if (queryParams.readableUser) {
      const user = JSON.parse(atob(queryParams.readableUser)) as IUser;

      confirmToken({ user: user });
    }
  }, [queryParams]);

  const { handleSubmit } = useFormik({
    initialValues: {},
    validationSchema,
    onSubmit: async () => {
      const ssoUrl = await getSSOUrl(null);
      if ((ssoUrl as any).error)
        return (
          <Snackbar message={'Something went wrong, please contact support'} />
        );
      window.location.href = (ssoUrl as any).data.url;
    },
  });

  if (isValidAuthorizedManager) {
    return <Navigate to={DashboardPage} replace />;
  }

  return (
    <div className={styles.root}>
      <ContentBox className={styles.content}>
        <CompanyLogo src={company.logo} responsive={true} />
        <form onSubmit={handleSubmit} className={styles.form}>
          <ActionRow style="space-between">
            <ButtonWithArrowIcon type="submit" disabled={isLoading}>
              {t('LoginPage.btn.submit')}
            </ButtonWithArrowIcon>
          </ActionRow>
          {isLoading && <Loader />}
        </form>
      </ContentBox>
    </div>
  );
};
